import { useCallback, useEffect, useRef, useState } from "react";
import {
  EASINGS,
  TRANSITIONS,
  runTransition,
} from "../../../canvas/design/transitions";
import { runMorph } from "../../../canvas/design/morph";
import { DEFAULT_TRANSITION, MAX_TRANSITION_MS, MIN_TRANSITION_MS } from "../../../canvas/design/slideDesign";
import SlideMiniPreview from "../SlideMiniPreview";
import { RangeField, ScopePicker, scopeLabel } from "./studioShared";

/** Play a transition between two stacked layers inside `root`, then show only B. */
const playBetween = ({ root, a, b, spec, fromData, toData, previous }) => {
  if (!root || !a || !b) return null;
  previous?.finish?.();
  a.style.visibility = "visible";
  b.style.visibility = "visible";
  const rect = root.getBoundingClientRect();
  let controller = null;
  if (spec.type === "morph" && fromData && toData) {
    controller = runMorph({ outEl: a, inEl: b, fromData, toData, duration: spec.duration, easing: spec.easing });
  }
  if (!controller) {
    controller = runTransition({
      outEl: a,
      inEl: b,
      stageEl: root,
      type: spec.type === "morph" ? "fade" : spec.type,
      duration: spec.duration,
      easing: spec.easing,
      direction: 1,
      width: rect.width,
      height: rect.height,
    });
  }
  controller.finished.then(() => {
    a.style.visibility = "hidden";
  });
  return controller;
};

function TransitionCard({ transition, active, spec, onSelect }) {
  const rootRef = useRef(null);
  const aRef = useRef(null);
  const bRef = useRef(null);
  const ctrlRef = useRef(null);
  const demo = () => {
    ctrlRef.current = playBetween({
      root: rootRef.current,
      a: aRef.current,
      b: bRef.current,
      spec: { ...spec, type: transition.id },
      previous: ctrlRef.current,
    });
  };
  useEffect(() => () => ctrlRef.current?.finish?.(), []);
  return (
    <button
      type="button"
      className={`cine-transition-card${active ? " is-active" : ""}`}
      onMouseEnter={demo}
      onFocus={demo}
      onClick={() => {
        onSelect(transition.id);
        demo();
      }}
      aria-pressed={active}
      title={transition.description}
    >
      <span className="cine-transition-demo" ref={rootRef}>
        <span className="cine-transition-demo-layer is-a" ref={aRef} style={{ visibility: "hidden" }}>
          <i />
          <i />
        </span>
        <span className="cine-transition-demo-layer is-b" ref={bRef}>
          <i />
          <i />
        </span>
      </span>
      <span className="cine-transition-name">{transition.label}</span>
    </button>
  );
}

export default function TransitionTab({
  currentTransition,
  isThemeDefault,
  previousElements,
  currentElements,
  slideCount,
  currentIndex,
  onApplyTransition,
}) {
  const initial = currentTransition || DEFAULT_TRANSITION;
  const [draft, setDraft] = useState(initial);
  const [scope, setScope] = useState("all");
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);
  const aRef = useRef(null);
  const bRef = useRef(null);
  const ctrlRef = useRef(null);

  useEffect(() => {
    setDraft(currentTransition || DEFAULT_TRANSITION);
  }, [currentIndex]);

  const preview = useCallback(
    (spec) => {
      ctrlRef.current = playBetween({
        root: rootRef.current,
        a: aRef.current,
        b: bRef.current,
        spec,
        fromData: previousElements,
        toData: currentElements,
        previous: ctrlRef.current,
      });
    },
    [currentElements, previousElements]
  );

  useEffect(() => () => ctrlRef.current?.finish?.(), []);

  const update = (patch) => {
    setDraft((d) => {
      const next = { ...d, ...patch };
      if (patch.type) window.requestAnimationFrame(() => preview(next));
      return next;
    });
  };

  const apply = async () => {
    setBusy(true);
    try {
      await onApplyTransition(draft, scope);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="cine-live-preview" ref={rootRef} style={{ perspective: 900 }}>
        <span className="cine-live-preview-badge">
          {currentIndex > 0 ? `Slide ${currentIndex} → ${currentIndex + 1}` : "Into slide 1"}
        </span>
        <div ref={aRef} style={{ position: "absolute", inset: 0, visibility: "hidden" }}>
          <SlideMiniPreview elements={previousElements || []} empty={!previousElements?.length} />
        </div>
        <div ref={bRef} style={{ position: "absolute", inset: 0 }}>
          <SlideMiniPreview elements={currentElements || []} />
        </div>
      </div>
      <div className="cine-row" style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 12.5, opacity: 0.7 }} className="cine-grow">
          This slide enters with{" "}
          <strong>{TRANSITIONS.find((t) => t.id === initial.type)?.label || "Smooth Fade"}</strong>
          {isThemeDefault ? " (theme default)" : ""}
        </span>
        <button type="button" className="cine-btn-secondary" onClick={() => preview(draft)}>
          ▶ Preview
        </button>
      </div>

      <span className="cine-studio-label">Transition</span>
      <div className="cine-transition-grid">
        {TRANSITIONS.map((t) => (
          <TransitionCard
            key={t.id}
            transition={t}
            active={draft.type === t.id}
            spec={draft}
            onSelect={(type) => update({ type })}
          />
        ))}
      </div>

      {draft.type === "morph" ? (
        <p className="cine-studio-sub" style={{ marginTop: 10 }}>
          Morph glides elements that appear on both slides (same image, histology slide, title or
          matching Morph ID) into their new position and size. Slides with nothing in common fade.
        </p>
      ) : null}

      {draft.type !== "none" ? (
        <>
          <span className="cine-studio-label">Timing</span>
          <RangeField
            label="Duration"
            value={draft.duration}
            min={MIN_TRANSITION_MS}
            max={MAX_TRANSITION_MS}
            step={50}
            unit="ms"
            onChange={(duration) => setDraft((d) => ({ ...d, duration }))}
            onCommit={() => preview(draft)}
          />
          <div className="cine-chips" style={{ marginTop: 6 }} role="radiogroup" aria-label="Easing">
            {EASINGS.map((e) => (
              <button
                key={e.id}
                type="button"
                role="radio"
                aria-checked={draft.easing === e.id}
                className={`cine-chip${draft.easing === e.id ? " is-active" : ""}`}
                onClick={() => {
                  const next = { ...draft, easing: e.id };
                  setDraft(next);
                  preview(next);
                }}
              >
                {e.label}
              </button>
            ))}
          </div>
        </>
      ) : null}

      <span className="cine-studio-label">Apply to</span>
      <ScopePicker value={scope} onChange={setScope} slideCount={slideCount} currentIndex={currentIndex} />
      <button
        type="button"
        className="cine-btn-primary"
        style={{ marginTop: 12, width: "100%" }}
        disabled={busy || (Array.isArray(scope) && !scope.length)}
        onClick={apply}
      >
        {busy ? "Applying…" : `Apply to ${scopeLabel(scope, slideCount)}`}
      </button>
      <p className="cine-studio-sub" style={{ marginTop: 10 }}>
        Viewers who ask their system for reduced motion see instant slide changes.
      </p>
    </>
  );
}
